import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import NoIndex from '../components/NoIndex';

/**
 * Auftragsverarbeitungsvertrag (AVV) nach Art. 28 DSGVO.
 *
 * Der Kunde (Verantwortlicher) beauftragt Saldify (CuiveMedia, Auftragsverarbeiter)
 * mit der Verarbeitung personenbezogener Daten seiner Geschaeftspartner.
 *
 * HINWEIS: Grundgeruest mit den Pflichtbestandteilen des Art. 28 Abs. 3 DSGVO –
 * ERSETZT KEINE anwaltliche Pruefung. Der finale, rechtsverbindliche Text ist von
 * Anwalt/DSB freizugeben. Bei inhaltlichen Aenderungen die AVV_VERSION in
 * apps/api/src/lib/legal.ts hochzaehlen (dann erneute Zustimmung aller Kunden).
 */
export default function AVV(): ReactElement {
  return (
    <div className="pt-24 pb-16 lg:pt-32 lg:pb-24">
      <NoIndex />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Auftragsverarbeitungsvertrag (AVV)</h1>
        <p className="text-sm text-secondary-500 mb-8">gemäß Art. 28 DSGVO</p>

        <div className="max-w-none space-y-8 text-secondary-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">1. Parteien &amp; Gegenstand</h2>
            <p>
              Dieser Vertrag wird geschlossen zwischen dem Nutzer (nachfolgend „Verantwortlicher“)
              und CuiveMedia, Inhaber Brian Brelöer, Siriusstraße 2, 12524 Berlin (nachfolgend
              „Auftragsverarbeiter“). Gegenstand ist die Verarbeitung personenbezogener Daten, die
              der Verantwortliche im Rahmen der Nutzung von Saldify verarbeitet (insbesondere Daten
              seiner Kunden, Lieferanten und sonstiger Geschäftspartner).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">2. Art, Zweck &amp; Dauer</h2>
            <p>
              Die Verarbeitung erfolgt zum Zweck der Bereitstellung der Buchhaltungs-,
              Rechnungs- und Belegverwaltungsfunktionen von Saldify, einschließlich automatischer
              Beleg- und Kontoauszugserkennung. Die Dauer entspricht der Laufzeit des
              Nutzungsverhältnisses.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">3. Art der Daten &amp; Betroffene</h2>
            <p>
              Verarbeitet werden u. a. Stammdaten (Namen, Anschriften), Kontakt- und Bankdaten
              (IBAN/BIC), Rechnungs- und Belegdaten. Betroffene Personen sind die Kunden,
              Lieferanten und Geschäftspartner des Verantwortlichen.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">4. Pflichten des Auftragsverarbeiters</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Verarbeitung nur auf dokumentierte Weisung des Verantwortlichen.</li>
              <li>Vertraulichkeit der zur Verarbeitung befugten Personen.</li>
              <li>Technische und organisatorische Maßnahmen nach Art. 32 DSGVO.</li>
              <li>Unterstützung bei Betroffenenrechten und Meldepflichten (Art. 33–36 DSGVO).</li>
              <li>Löschung oder Rückgabe der Daten nach Vertragsende.</li>
              <li>Nachweis der Einhaltung und Ermöglichung von Überprüfungen.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">5. Unterauftragsverarbeiter</h2>
            <p>
              Der Verantwortliche stimmt dem Einsatz von Unterauftragsverarbeitern gemäß der
              jeweils aktuellen{' '}
              <Link to="/subprozessoren" className="text-primary-600 hover:underline">Liste der Sub-Prozessoren</Link>{' '}
              zu (u. a. Google/Gemini für die Belegerkennung). Über Änderungen wird vorab informiert;
              es besteht ein Widerspruchsrecht. Drittlandübermittlungen erfolgen auf Basis der
              EU-Standardvertragsklauseln.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">6. Technische &amp; organisatorische Maßnahmen (TOM)</h2>
            <p>
              Verschlüsselung der Übertragung (TLS) und sensibler Geheimnisse, Zugriffskontrolle
              über Authentifizierung und Mandantentrennung, Serverstandort Deutschland,
              revisionssichere Protokollierung sicherheitsrelevanter Vorgänge.
            </p>
          </section>

          <p className="text-xs text-secondary-500">
            Grundgerüst mit den Pflichtbestandteilen des Art. 28 Abs. 3 DSGVO – ersetzt keine
            anwaltliche Prüfung. Maßgeblich ist die jeweils im Produkt akzeptierte Fassung.
          </p>
        </div>
      </div>
    </div>
  );
}
